import { FlexContainer, LoadingPage } from '@act/storybook';
import type { ReportDetailDto } from '@act/shared/data-transfer-objects';
import { formatWarehouseMeasurementPeriodLabel } from '@act/shared/data-transfer-objects';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import ReportBuilderHeaderActions from '../components/ReportBuilderHeaderActions';
import ReportBuilderSectionEditor from '../components/ReportBuilderSectionEditor';
import ReportBuilderSectionList from '../components/ReportBuilderSectionList';
import ReportBuilderTemplateFooter from '../components/ReportBuilderTemplateFooter';
import ReportStatusBadge from '../components/ReportStatusBadge';
import ReportsUnauthorized from '../components/ReportsUnauthorized';
import { useReportBuilderSections } from '../hooks/useReportBuilderSections.hook';
import { useReportWarehouseRefresh } from '../hooks/useReportWarehouseRefresh.hook';
import { useReportsPermissions } from '../hooks/useReportsPermissions.hook';
import { formatReportTimestamp } from '../utils/reportDisplay.util';
import { normalizeReportCadence } from '../preview/reportPreview.adapter.util';

/**
 * Report builder: drag-and-drop sections, toggles, and narrative/callout editors.
 */
const ReportBuilderPage = () => {
  const { reportId = '' } = useParams<{ reportId: string }>();
  const toast = useToast();
  const { canEdit } = useReportsPermissions();

  const [report, reportLoading, loadReport, reportError] = useApiCall(
    reportsRequests.getReport,
  );
  const [customer, , loadCustomer] = useApiCall(reportsRequests.getCustomer);
  const [, savingSection, patchSection] = useApiCall(
    reportsRequests.updateReportSection,
  );
  const [, reordering, reorderSections] = useApiCall(
    reportsRequests.reorderReportSections,
  );
  const [, archiving, archiveReport] = useApiCall(reportsRequests.archiveReport);
  const [, unarchiving, unarchiveReport] = useApiCall(
    reportsRequests.unarchiveReport,
  );
  const [savedTemplate, savingTemplate, saveTemplateFromReport] = useApiCall(
    reportsRequests.createTemplateFromReport,
  );

  const [templateName, setTemplateName] = useState('');

  const {
    localSections,
    selectedSectionId,
    setSelectedSectionId,
    selectedSection,
    handleIncludedChange,
    handleCalloutEnabledChange,
    handleClinicalInsightEnabledChange,
    handleFinancialInsightEnabledChange,
    handleFieldBlur,
    handleInsightFieldChange,
    handleInsightFieldBlur,
    handleReorder,
  } = useReportBuilderSections(
    reportId,
    report ?? null,
    patchSection,
    reorderSections,
  );

  const refreshReport = useCallback(() => {
    if (reportId) {
      loadReport({ params: { id: reportId } });
    }
  }, [reportId, loadReport]);

  const { canRefreshData, refreshingSnapshots, handleRefreshData } =
    useReportWarehouseRefresh({
      reportId,
      report,
      refreshReport,
      toast,
    });

  useEffect(() => {
    refreshReport();
  }, [refreshReport, reportId]);

  useEffect(() => {
    if (report?.customerId) {
      loadCustomer({ params: { id: report.customerId } });
    }
  }, [report?.customerId, loadCustomer]);

  useEffect(() => {
    if (savedTemplate && !savingTemplate) {
      toast(`Template "${savedTemplate.name}" saved`, 'success');
      setTemplateName('');
    }
  }, [savedTemplate, savingTemplate, toast]);

  const handleArchiveToggle = (detail: ReportDetailDto) => {
    if (detail.status === 'archived') {
      unarchiveReport({ params: { id: reportId } });
      toast('Report restored', 'success');
    } else {
      archiveReport({ params: { id: reportId } });
      toast('Report archived', 'success');
    }
    refreshReport();
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      toast('Template name is required', 'error');
      return;
    }
    saveTemplateFromReport({
      params: { reportId },
      body: { name: templateName.trim() },
    });
  };

  if (!canEdit) {
    return <ReportsUnauthorized />;
  }

  if (!reportLoading && !report && reportError) {
    return (
      <FlexContainer direction="column" gap={2} padding={2}>
        <Typography variant="h6">Report not found</Typography>
        <Typography variant="body2" color="text.secondary">
          This report may have been deleted or you may not have access.
        </Typography>
        <Link to="/">Back to reports</Link>
      </FlexContainer>
    );
  }

  if (reportLoading || !report) {
    return <LoadingPage />;
  }

  const measurementPeriodLabel = report
    ? formatWarehouseMeasurementPeriodLabel(
        report.measurementPeriod ?? report.reportingPeriod ?? '',
      )
    : '';

  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      flexGrow={1}
      minHeight="100%"
      overflow="hidden"
    >
      <FlexContainer
        direction="column"
        gap={1.5}
        width="100%"
        height="auto"
        overflowAllVisible={true}
        flexShrink={0}
      >
        <Link to="/">
          <Typography variant="body2" color="text.secondary">
            ← All reports
          </Typography>
        </Link>
        <FlexContainer
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap={true}
          gap={2}
          width="100%"
          height="auto"
          overflowAllVisible={true}
        >
          <FlexContainer
            direction="column"
            gap={0.5}
            height="auto"
            overflowAllVisible={true}
          >
            <Typography variant="h4" component="h1">
              {report.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {customer?.name ?? report.customerId} · {measurementPeriodLabel}
            </Typography>
            <FlexContainer
              direction="row"
              gap={1.5}
              alignItems="center"
              height="auto"
              overflowAllVisible={true}
            >
              <ReportStatusBadge status={report.status} />
              <Typography variant="caption" color="text.secondary">
                Updated {formatReportTimestamp(report.updatedAt)}
              </Typography>
            </FlexContainer>
          </FlexContainer>
          <ReportBuilderHeaderActions
            reportId={reportId}
            status={report.status}
            canRefreshData={canRefreshData}
            refreshingData={refreshingSnapshots}
            archiving={archiving}
            unarchiving={unarchiving}
            onRefreshData={handleRefreshData}
            onArchiveToggle={() => handleArchiveToggle(report)}
          />
        </FlexContainer>
      </FlexContainer>

      <FlexContainer
        direction="row"
        gap={2}
        width="100%"
        flexGrow={1}
        minHeight="0"
        alignItems="stretch"
        overflow="hidden"
      >
        <ReportBuilderSectionList
          sections={localSections}
          selectedSectionId={selectedSectionId}
          savingSection={savingSection}
          reordering={reordering}
          onSelect={setSelectedSectionId}
          onIncludedChange={handleIncludedChange}
          onCalloutEnabledChange={handleCalloutEnabledChange}
          onReorder={handleReorder}
        />
        <FlexContainer
          direction="column"
          flexGrow={1}
          minWidth="0"
          minHeight="0"
          width="100%"
          height="100%"
          overflowY="auto"
          overflowX="hidden"
        >
          <ReportBuilderSectionEditor
          section={selectedSection}
          customer={customer}
          reportingPeriod={report.reportingPeriod}
          measurementPeriod={
            report.measurementPeriod ?? report.reportingPeriod ?? ''
          }
          reportId={reportId}
          cadence={normalizeReportCadence(report.cadence)}
          savingSection={savingSection}
          onIncludedChange={handleIncludedChange}
          onCalloutEnabledChange={handleCalloutEnabledChange}
          onClinicalInsightEnabledChange={handleClinicalInsightEnabledChange}
          onFinancialInsightEnabledChange={handleFinancialInsightEnabledChange}
          onFieldBlur={handleFieldBlur}
          onInsightFieldChange={handleInsightFieldChange}
          onInsightFieldBlur={handleInsightFieldBlur}
        />
        </FlexContainer>
      </FlexContainer>

      <ReportBuilderTemplateFooter
        templateName={templateName}
        savingTemplate={savingTemplate}
        onTemplateNameChange={setTemplateName}
        onSaveTemplate={handleSaveTemplate}
      />
    </FlexContainer>
  );
};

export default ReportBuilderPage;
